import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Search, Star, Edit, Trash2, Loader2, Zap } from "lucide-react";
import { api, fileUrl, formatPrice } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { MatchesModal } from "@/components/admin/MatchesModal";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const TABS = [
  { value: "all", label: "All" },
  { value: "available", label: "Available" },
  { value: "sold", label: "Sold" },
  { value: "not_active", label: "Hidden" },
];

export default function AdminMachines() {
  const [machines, setMachines] = useState([]);
  const [tab, setTab] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [buyersFor, setBuyersFor] = useState(null);

  const load = () => {
    setLoading(true);
    const params = {};
    if (tab !== "all") params.status = tab;
    if (typeFilter !== "all") params.listing_type = typeFilter;
    if (search) params.search = search;
    api.get("/admin/machines", { params }).then((r) => setMachines(r.data)).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [tab, typeFilter, search]);

  const changeStatus = async (id, status) => {
    try { await api.patch(`/admin/machines/${id}/status`, { status }); toast.success("Status updated"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const toggleFeatured = async (m) => {
    try { await api.patch(`/admin/machines/${m.id}/featured`, { featured: !m.featured }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const remove = async (id) => {
    try { await api.delete(`/admin/machines/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error("Failed to delete"); }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-display text-2xl font-extrabold text-slate-900">Machines</h1>
        <Link to="/admin/machines/new" data-testid="add-machine-btn" className="inline-flex items-center gap-1.5 rounded-full bg-brand px-5 py-2.5 text-sm font-bold text-white">
          <Plus size={16} /> Add Machine
        </Link>
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-3">
        <div className="flex gap-1 rounded-lg border border-slate-200 bg-white p-1">
          {TABS.map((t) => (
            <button
              key={t.value}
              data-testid={`machines-tab-${t.value}`}
              onClick={() => setTab(t.value)}
              className={`rounded-md px-3 py-1.5 text-sm font-semibold ${tab === t.value ? "bg-brand text-white" : "text-slate-600"}`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="relative ml-auto w-full sm:w-64">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input data-testid="machines-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search machines..." className="pl-9" />
        </div>
        <div className="w-full sm:w-44">
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger data-testid="machines-type-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Sections</SelectItem>
              <SelectItem value="used">Used Machines</SelectItem>
              <SelectItem value="new">New Machines</SelectItem>
              <SelectItem value="spare">Spare Parts</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
      ) : (
        <div className="mt-5 space-y-3">
          {machines.length === 0 && <p className="py-12 text-center text-slate-500">No machines found.</p>}
          {machines.map((m) => (
            <div key={m.id} data-testid={`admin-machine-row-${m.machine_code}`} className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-white p-3 sm:flex-row sm:items-center">
              <div className="flex flex-1 items-center gap-3">
                <div className="h-16 w-20 shrink-0 overflow-hidden rounded-lg bg-slate-100">
                  {m.images?.[0]?.url && <img src={fileUrl(m.images[0].url)} alt="" className="h-full w-full object-cover" />}
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs text-slate-400">{m.machine_code}</span>
                    {m.listing_type && m.listing_type !== "used" && (
                      <span className="rounded bg-slate-900 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
                        {m.listing_type === "new" ? "New" : "Spare"}
                      </span>
                    )}
                    {m.featured && <Star size={13} className="fill-amber-400 text-amber-400" />}
                  </div>
                  <p className="truncate font-semibold text-slate-900">{m.name}</p>
                  <p className="text-xs text-slate-500">{m.brand} • {m.category_name} • {formatPrice(m.price, m.price_type)}</p>
                  <p className="text-xs text-slate-400">Seller: {m.seller_name} • {m.view_count || 0} views</p>
                  {m.buyer_match_count > 0 && <span data-testid={`machine-match-count-${m.machine_code}`} className="mt-1 inline-block rounded-full bg-orange-100 px-2 py-0.5 text-[10px] font-bold text-orange-700">⚡ {m.buyer_match_count} buyer{m.buyer_match_count > 1 ? "s" : ""} matched</span>}
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <div className="w-36">
                  <Select value={m.status} onValueChange={(v) => changeStatus(m.id, v)}>
                    <SelectTrigger data-testid={`status-select-${m.machine_code}`} className="h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="available">Available</SelectItem>
                      <SelectItem value="sold">Sold</SelectItem>
                      <SelectItem value="not_active">Hidden</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <button
                  data-testid={`feature-toggle-${m.machine_code}`}
                  onClick={() => toggleFeatured(m)}
                  title="Toggle featured"
                  className={`rounded-lg border p-2 ${m.featured ? "border-amber-300 bg-amber-50 text-amber-500" : "border-slate-300 text-slate-400"}`}
                >
                  <Star size={16} className={m.featured ? "fill-amber-400" : ""} />
                </button>
                <Link to={`/admin/machines/${m.id}`} data-testid={`edit-machine-${m.machine_code}`} className="rounded-lg border border-slate-300 p-2 text-slate-600 hover:bg-slate-50">
                  <Edit size={16} />
                </Link>
                <button data-testid={`find-buyers-${m.machine_code}`} onClick={() => setBuyersFor(m)} title="Find matching buyers" className="rounded-lg border border-orange-200 bg-orange-50 p-2 text-orange-500 hover:bg-orange-100">
                  <Zap size={16} />
                </button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <button data-testid={`delete-machine-${m.machine_code}`} className="rounded-lg border border-red-200 p-2 text-red-500 hover:bg-red-50"><Trash2 size={16} /></button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete machine?</AlertDialogTitle>
                      <AlertDialogDescription>This will permanently delete {m.name} and its enquiries. To temporarily hide instead, set status to Hidden.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={() => remove(m.id)} className="bg-red-600">Delete</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          ))}
        </div>
      )}
      {buyersFor && <MatchesModal mode="buyers" source={{ id: buyersFor.id, label: `${buyersFor.machine_code} · ${buyersFor.name}` }} onClose={() => setBuyersFor(null)} />}
    </div>
  );
}
